import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../axiosinstance";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../Config"; // Adjust the path based on your file structure
import { Snackbar, Alert } from "@mui/material";
import { Button, Box, Typography } from "@mui/material";
import { FiUpload } from "react-icons/fi";
import { validateGST, validateMobile, validateIFSC, validateAccountNumber } from "../helperFunctions";
export default function EditSubsidary({ isSidebarExpanded }) {
  const { subsidiary_id } = useParams();
  const FILE_BASE_URL = API_URL.replace(/\/+$/, "");
  const logoInputRef = useRef(null);
  const qrInputRef = useRef(null);

  const [existingLogoPath, setExistingLogoPath] = useState("");
  const [existingQrPath, setExistingQrPath] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeQr, setRemoveQr] = useState(false);
  const [logoFilePreview, setLogoFilePreview] = useState(null);
  const [qrFilePreview, setQrFilePreview] = useState(null);

  const navigate = useNavigate();

  const toFileUrl = (path) => {
    if (!path) return null;
    const normalizedPath = String(path).replaceAll("\\", "/");
    if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath; // already absolute
    return `${FILE_BASE_URL}/${normalizedPath.replace(/^\/+/, "")}`;
  };

  const getFileNameFromPath = (p) => {
    if (!p) return "";
    try {
      // handle absolute URLs
      const u = new URL(toFileUrl(p));
      const last = u.pathname.split("/").pop();
      return decodeURIComponent(last || "");
    } catch {
      // handle relative paths
      const s = String(p).replaceAll("\\", "/");
      const last = s.split("/").pop();
      return decodeURIComponent(last || "");
    }
  };

  const isImageFile = (file) =>
    !!file && file.type && file.type.startsWith("image/");

  const [subsidaryData, setSubsidaryData] = useState({
    NameofCompany: "",
    NameofOwner: "",
    Address: "",
    MobileNo: "",
    Email: "",
    GstNO: "",
    InvoicingLimit: "",
    TagName: "",
    AccountNumber: "",
    AccountHolder: "",
    IFSCCode: "",
    Branch: "",
    PanNumber: "",
    VirtualPayementAddress: "",
  });
  
  const [isGstYes, setIsGstYes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // const [logoPreview, setLogoPreview] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  useEffect(() => {
    return () => {
      if (logoFilePreview) URL.revokeObjectURL(logoFilePreview);
      if (qrFilePreview) URL.revokeObjectURL(qrFilePreview);
    };
  }, [logoFilePreview, qrFilePreview]);

  // Fetch subsidiary data on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchSubsidiaryData = async () => {
      try {
        setIsLoading(true);

        const response = await axiosInstance.get(
          `/api/get_subsidiary_by_id/${subsidiary_id}`
        );

        if (!isMounted) return; // prevent setting state if unmounted
        const data = response.data.result;

        setSubsidaryData({
          NameofCompany: data.subsidiary_name,
          NameofOwner: data.owner,
          Address: data.address,
          MobileNo: data.contact_no,
          Email: data.email_id,
          GstNO: data.gstNumber || "",
          InvoicingLimit: data.invoicing_limit,
          TagName: data.tag_name || "",
          AccountHolder: data.account_holder,
          AccountNumber: data.account_number,
          Branch: data.branch,
          IFSCCode: data.ifsc_code,
          PanNumber: data.pan_no,
          VirtualPayementAddress: data.vpa,
        });

        setIsGstYes(
          data?.hasGST === true || String(data?.hasGST).toLowerCase() === "yes"
        );

        setExistingLogoPath(data.logo || "");
        setExistingQrPath(data.qr_code || "");
        setLogoFile(null);
        setQrFile(null);
        if (logoFilePreview) URL.revokeObjectURL(logoFilePreview);
        if (qrFilePreview) URL.revokeObjectURL(qrFilePreview);
        setLogoFilePreview(null);
        setQrFilePreview(null);
        setRemoveLogo(false);
        setRemoveQr(false);
        if (logoInputRef.current) logoInputRef.current.value = "";
        if (qrInputRef.current) qrInputRef.current.value = "";
      } catch (error) {
        console.error("Error fetching subsidiary data:", error);
        if (isMounted) {
          setSnackbarMessage("Failed to fetch subsidiary data");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchSubsidiaryData();

    return () => {
      isMounted = false;
    };
  }, [subsidiary_id]);

  const handleGstChange = (e) => {
    const isYes = e.target.value === "yes";
    setIsGstYes(isYes);
    // Root cause: switching GST to "No" previously set "Not Applicable", which still
    // triggered GST format validation in submit flow. Keep GST number empty when "No".
    setSubsidaryData((prev) => ({
      ...prev,
      GstNO: isYes ? prev.GstNO : "",
    }));
    if (!isYes) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        GstNO: "",
      }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSubsidaryData({ ...subsidaryData, [name]: value });

    // Clear error if user starts typing in GstNO
    if (name === "GstNO" && errors.GstNO) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        GstNO: "",
      }));
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!isImageFile(file)) {
      setSnackbarMessage("Only image files are allowed.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setErrors((prev) => ({ ...prev, logoFile: "Only image files are allowed." }));
      if (logoInputRef.current) logoInputRef.current.value = "";
      return;
    }

    if (logoFilePreview) URL.revokeObjectURL(logoFilePreview);
    setLogoFile(file);
    setLogoFilePreview(URL.createObjectURL(file));
    setRemoveLogo(false);
    setErrors((prev) => ({ ...prev, logoFile: "" }));
  };

  const handleQrFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!isImageFile(file)) {
      setSnackbarMessage("Only image files are allowed.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setErrors((prev) => ({ ...prev, qrFile: "Only image files are allowed." }));
      if (qrInputRef.current) qrInputRef.current.value = "";
      return;
    }

    if (qrFilePreview) URL.revokeObjectURL(qrFilePreview);
    setQrFile(file);
    setQrFilePreview(URL.createObjectURL(file));
    setRemoveQr(false);
    setErrors((prev) => ({ ...prev, qrFile: "" }));
  };

  const handleRemoveLogo = () => {
    if (logoFilePreview) URL.revokeObjectURL(logoFilePreview);
    setLogoFilePreview(null);
    setLogoFile(null);
    setRemoveLogo(true);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleRemoveQr = () => {
    if (qrFilePreview) URL.revokeObjectURL(qrFilePreview);
    setQrFilePreview(null);
    setQrFile(null);
    setRemoveQr(true);
    if (qrInputRef.current) qrInputRef.current.value = "";
  };

  const currentLogoPreview =
    logoFilePreview || (!removeLogo && existingLogoPath ? toFileUrl(existingLogoPath) : null);
  const currentLogoName =
    logoFile?.name ||
    (!removeLogo && existingLogoPath ? getFileNameFromPath(existingLogoPath) : "");
  const currentQrPreview =
    qrFilePreview || (!removeQr && existingQrPath ? toFileUrl(existingQrPath) : null);
  const currentQrName =
    qrFile?.name || (!removeQr && existingQrPath ? getFileNameFromPath(existingQrPath) : "");

  const renderPreviewThumbnail = (previewSrc, altText, onRemove, canRemove = true) => (
    <Box
      mt={2}
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        display: "inline-block",
        p: 0.5,
        position: "relative",
      }}
    >
      {canRemove && (
        <Box
          component="button"
          type="button"
          title="Remove"
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: "6px",
            right: "6px",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            border: "1px solid #d1d5db",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            fontSize: "16px",
            color: "#374151",
            p: 0,
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          ×
        </Box>
      )}
      <img
        src={previewSrc}
        alt={altText}
        style={{ width: 100, height: 50, objectFit: "contain" }}
      />
    </Box>
  );
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const vpaRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9]+$/; // someone@upi format

  

 const fieldRules = {
  NameofCompany: { required: true, max: 100, label: "Company Name" },
  NameofOwner: { required: true, max: 50, label: "Proprietor Name" },
  Address: { required: true, max: 250 },
  MobileNo: { required: true, custom: validateMobile, label: "Mobile Number" },
  Email: { required: true, pattern: emailRegex },
  // Root cause: GST validator must only run when GST is applicable.
  GstNO: { required: isGstYes, label: "GST Number", custom: isGstYes ? validateGST : null },
  InvoicingLimit: { required: true, numeric: true, label: "Invoicing Limit" },
  TagName: { required: true, max: 50, label: "Tag Name" },
  AccountNumber: { required: true, label: "Account Number", custom: validateAccountNumber },
  AccountHolder: { required: true, label: "Account Holder" },
  IFSCCode: {
    required: true,
    label: "IFSC Code",
    custom: validateIFSC
  },
  Branch: { required: false },
  PanNumber: { required: true, pattern: panRegex, label: "PAN Number" },
  VirtualPayementAddress: {
    required: false,
    pattern: vpaRegex,
    max: 100,
    label: "Virtual Payment Address",
  },
};
const formatField = (field) =>
  field.replace(/([A-Z])/g, " $1").trim();

const validateForm = () => {
  const errors = {};

  Object.keys(fieldRules).forEach((field) => {
    const value = subsidaryData[field]?.toString().trim();
    const rules = fieldRules[field];
    const label = rules.label || formatField(field);
    // Required
    if (rules.required && !value) {
      errors[field] = `${label} is required`;
      return;
    }

    // Max length
    if (value && rules.max && value.length > rules.max) {
      errors[field] = `${label} cannot exceed ${rules.max} characters`;
      return;
    }

    // Pattern
    if (value && rules.pattern && !rules.pattern.test(value)) {
      errors[field] = `Invalid ${label}`;
      return;
    }

    // Numeric
    if (value && rules.numeric && isNaN(value)) {
      errors[field] = `${label} must be a number`;
      return;
    }

    // Custom validator (mobile)
    if (value && rules.custom) {
      const customError = rules.custom(value);
      if (customError) {
        errors[field] = customError;
      }
    }
  });

  // File validation
  if (qrFile && !qrFile.type?.startsWith("image/")) {
    errors.qrFile = "Only image files are allowed.";
  }

  if (!logoFile && removeLogo) {
    errors.logo = "Please upload logo";
  }
  
  return errors;
};
//Old
  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   // Form Validation
  //   const validationErrors = {};
  //   Object.keys(subsidaryData).forEach((key) => {
  //     if (!subsidaryData[key] && key !== "GstNO" && key !== "Branch") {
  //       validationErrors[key] = `${key} is required`;
  //     }
  //   });

  //   if (isGstYes && !subsidaryData.GstNO) {
  //     validationErrors.GstNO = "GST Number is required";
  //   }

  //   if (Object.keys(validationErrors).length > 0) {
  //     setErrors(validationErrors);
  //     return;
  //   }

  //   setIsLoading(true);

  //   try {
  //     // Create FormData object
  //     const formData = new FormData();

  //     // Append all form fields
  //     formData.append("name", subsidaryData.NameofCompany);
  //     formData.append("owner", subsidaryData.NameofOwner);
  //     formData.append("address", subsidaryData.Address);
  //     formData.append("contact_no", subsidaryData.MobileNo);
  //     formData.append("email_id", subsidaryData.Email);
  //     formData.append("hasGST", isGstYes ? "Yes" : "No");
  //     formData.append("gst_no", isGstYes ? subsidaryData.GstNO : "");
  //     formData.append("invoicing_limit", subsidaryData.InvoicingLimit);
  //     formData.append("tag_name", subsidaryData.TagName);
  //     formData.append("account_holder", subsidaryData.AccountHolder);
  //     formData.append("account_number", subsidaryData.AccountNumber);
  //     formData.append("branch", subsidaryData.Branch);
  //     formData.append("ifsc_code", subsidaryData.IFSCCode);
  //     formData.append("pan_no", subsidaryData.PanNumber);
  //     formData.append("vpa", subsidaryData.VirtualPayementAddress);

  //     formData.append("remove_logo", removeLogo ? "true" : "false");
  //     formData.append("remove_qr", removeQr ? "true" : "false");
  //     if (logoFile) formData.append("logo", logoFile);
  //     if (qrFile) formData.append("qr_code", qrFile);

  //     // Get fresh token
  //     const token = localStorage.getItem("authKey");

  //     const response = await axiosInstance.put(
  //       `/api/edit_subsidiary/${subsidiary_id}`,
  //       formData,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           // Don't set Content-Type, let axios handle it
  //         },
  //       }
  //     );

  //     if (response.status === 200) {
  //       setSnackbarMessage("Subsidiary updated successfully");
  //       setSnackbarSeverity("success");
  //       setSnackbarOpen(true);
  //       setTimeout(() => navigate("/subsidary"), 1500);
  //     } else {
  //       throw new Error("Failed to update subsidiary");
  //     }
  //   } catch (error) {
  //     console.error("Error updating subsidiary:", error);
  //     setSnackbarMessage(
  //       error.response?.data?.error ||
  //         error.response?.data?.message ||
  //         "Failed to update subsidiary"
  //     );
  //     setSnackbarSeverity("error");
  //     setSnackbarOpen(true);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
// New
const handleSubmit = async (e) => {
    e.preventDefault();

    // Form Validation
    const validationErrors = validateForm()

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Create FormData object
      const formData = new FormData();

      // Append all form fields
      formData.append("name", subsidaryData.NameofCompany);
      formData.append("owner", subsidaryData.NameofOwner);
      formData.append("address", subsidaryData.Address);
      formData.append("contact_no", subsidaryData.MobileNo);
      formData.append("email_id", subsidaryData.Email);
      formData.append("hasGST", isGstYes ? "Yes" : "No");
      formData.append("gst_no", isGstYes ? subsidaryData.GstNO : "");
      formData.append("invoicing_limit", subsidaryData.InvoicingLimit);
      formData.append("tag_name", subsidaryData.TagName);
      formData.append("account_holder", subsidaryData.AccountHolder);
      formData.append("account_number", subsidaryData.AccountNumber);
      formData.append("branch", subsidaryData.Branch);
      formData.append("ifsc_code", subsidaryData.IFSCCode);
      formData.append("pan_no", subsidaryData.PanNumber);
      formData.append("vpa", subsidaryData.VirtualPayementAddress);

      formData.append("remove_logo", removeLogo ? "true" : "false");
      formData.append("remove_qr", removeQr ? "true" : "false");
      if (logoFile) formData.append("logo", logoFile);
      if (qrFile) formData.append("qr_code", qrFile);

      // Temporary debug log for GST toggle/save verification.
      const payloadPreview = Object.fromEntries(formData.entries());
      console.log("Edit Subsidiary GST submit debug:", {
        isGstYes,
        gstNumberValue: subsidaryData.GstNO,
        payload: payloadPreview,
      });

      // Get fresh token
      const token = localStorage.getItem("authKey");

      const response = await axiosInstance.put(
        `/api/edit_subsidiary/${subsidiary_id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Don't set Content-Type, let axios handle it
          },
        }
      );

      if (response.status === 200) {
        setSnackbarMessage("Subsidiary updated successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => navigate("/subsidary"), 1500);
      } else {
        throw new Error("Failed to update subsidiary");
      }
    } catch (error) {
      console.error("Error updating subsidiary:", error);
      setSnackbarMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update subsidiary"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = () => {
    window.history.back();
  };

  return (
    <div
      className={`table-container subsidiary-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body subsidiary-form-content">
        <div className="subsidiary-page-heading">
          <h1 className="form-title jost-bold">Edit Subsidiary</h1>
          <p className="subsidiary-page-subtitle">
            Update subsidiary details, tax information and banking details.
          </p>
        </div>
        <div className="subsidiary-form-card">
        <div className="form-body-subsidary subsidiary-form-body">
          <form onSubmit={handleSubmit} className="form-box subsidiary-form-box">
            {/* Name of Company */}
            <div className="form-body-container subsidiary-form-sections">
              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">Basic Details</h2>
                    <p className="subsidiary-section-description">
                      Update company, owner and contact information.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label">
                  Name of Company <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="NameofCompany"
                  className="input-field"
                  placeholder="Enter Name of Company"
                  value={subsidaryData.NameofCompany}
                  onChange={handleInputChange}
                />
                {errors.NameofCompany && (
                  <p className="error">{errors.NameofCompany}</p>
                )}
              </div>

              {/* Name of Owner */}
              <div className="form-group">
                <label className="form-label">
                  Name of Proprietor/Owner <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="NameofOwner"
                  className="input-field"
                  placeholder="Enter Name of Owner"
                  value={subsidaryData.NameofOwner}
                  onChange={handleInputChange}
                />
                {errors.NameofOwner && (
                  <p className="error">{errors.NameofOwner}</p>
                )}
              </div>

              {/* Address */}
              <div className="form-group subsidiary-field-span-2">
                <label className="form-label">
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="Address"
                  className="input-field"
                  placeholder="Enter Address"
                  value={subsidaryData.Address}
                  onChange={handleInputChange}
                />
                {errors.Address && <p className="error">{errors.Address}</p>}
              </div>

              {/* Mobile No */}
              <div className="form-group">
                <label className="form-label">
                  Mobile No <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  name="MobileNo"
                  className="input-field"
                  placeholder="Enter Mobile Number"
                  value={subsidaryData.MobileNo}
                  onChange={handleInputChange}
                />
                {errors.MobileNo && <p className="error">{errors.MobileNo}</p>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="Email"
                  className="input-field"
                  placeholder="Enter Email"
                  value={subsidaryData.Email}
                  onChange={handleInputChange}
                />
                {errors.Email && <p className="error">{errors.Email}</p>}
              </div>

              {/* Tag Name */}
              <div className="form-group">
                <label className="form-label">
                  Tag Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="TagName"
                  className="input-field"
                  placeholder="Enter Tag Name"
                  value={subsidaryData.TagName}
                  onChange={handleInputChange}
                />
                {errors.TagName && <p className="error">{errors.TagName}</p>}
              </div>
              </section>

              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">
                      Tax & Billing Details
                    </h2>
                    <p className="subsidiary-section-description">
                      Keep GST preferences and invoicing rules aligned.
                    </p>
                  </div>
                </div>
              {/* GST */}
              <div className="form-group subsidiary-field-span-2">
                <label className="form-label">
                  GST No. <span className="required">*</span>
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="GstNO"
                      value="yes"
                      checked={isGstYes}
                      onChange={handleGstChange}
                    />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="GstNO"
                      value="no"
                      checked={!isGstYes}
                      onChange={handleGstChange}
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Display GST input field only if GST is selected */}
              {isGstYes && (
                <div className="form-group subsidiary-field-span-2">
                  <label className="form-label"></label>
                  <input
                    type="text"
                    name="GstNO"
                    className="input-field"
                    placeholder="Enter GST Number"
                    value={subsidaryData.GstNO}
                    onChange={handleInputChange}
                  />
                  {errors.GstNO && <p className="error">{errors.GstNO}</p>}
                </div>
              )}

              {/* Invoicing Limit */}
              <div className="form-group">
                <label className="form-label">
                  Invoicing Limit <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="InvoicingLimit"
                  className="input-field"
                  placeholder="Enter Invoicing Limit"
                  value={subsidaryData.InvoicingLimit}
                  onChange={handleInputChange}
                />
                {errors.InvoicingLimit && (
                  <p className="error">{errors.InvoicingLimit}</p>
                )}
              </div>
              </section>

              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">
                      Bank / Payment Details
                    </h2>
                    <p className="subsidiary-section-description">
                      Update banking, branch and payment identity details.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label">
                  Account Number <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="AccountNumber"
                  className="input-field"
                  placeholder="Enter Account Number"
                  value={subsidaryData.AccountNumber}
                  onChange={handleInputChange}
                />
                {errors.AccountNumber && (
                  <p className="error">{errors.AccountNumber}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Account Holder <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="AccountHolder"
                  className="input-field"
                  placeholder="Enter Account Holder"
                  value={subsidaryData.AccountHolder}
                  onChange={handleInputChange}
                />
                {errors.AccountHolder && (
                  <p className="error">{errors.AccountHolder}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  IFSC Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="IFSCCode"
                  className="input-field"
                  placeholder="Enter IFSC Code"
                  value={subsidaryData.IFSCCode}
                  onChange={handleInputChange}
                />
                {errors.IFSCCode && <p className="error">{errors.IFSCCode}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Bank Branch</label>
                <input
                  type="text"
                  name="Branch" // ✅ Corrected to match state key
                  className="input-field"
                  placeholder="Enter Bank Branch"
                  value={subsidaryData.Branch}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  PAN Number <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="PanNumber" // Ensure it matches the state key
                  className="input-field"
                  placeholder="Enter PAN Number"
                  value={subsidaryData.PanNumber} // Correct state reference
                  onChange={handleInputChange}
                />
                {errors.PanNumber && (
                  <p className="error">{errors.PanNumber}</p>
                )}{" "}
              </div>

              <div className="form-group">
                <label className="form-label">
                  UPI ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="VirtualPayementAddress"
                  className="input-field"
                  placeholder="Enter UPI ID"
                  value={subsidaryData.VirtualPayementAddress}
                  onChange={handleInputChange}
                />
                {errors.VirtualPayementAddress && (
                  <p className="error">{errors.VirtualPayementAddress}</p>
                )}
              </div>
              </section>
              <section className="subsidiary-form-section subsidiary-form-section-last">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">Uploads</h2>
                    <p className="subsidiary-section-description">
                      Manage logo and QR assets without changing existing upload
                      behavior.
                    </p>
                  </div>
                </div>
            <div className="form-group subsidiary-upload-group">
              <label className="form-label">
                Upload Logo*
              </label>

              {/* hidden file input */}
              <input
                ref={logoInputRef}
                accept="image/png,image/jpeg,image/jpg,image/webp"
                style={{ display: "none" }}
                id="edit-upload-logo"
                type="file"
                onChange={handleLogoFileChange}
              />
              {currentLogoName && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  File: {currentLogoName}
                </Typography>
              )}

              {/* MUI button trigger */}
              <label htmlFor="edit-upload-logo">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<FiUpload />}
                  sx={{ color: "#4e4c4c", borderColor: "rgb(189,189,189)" }}
                >
                  {logoFile || currentLogoPreview ? "Change Logo" : "Upload Logo"}
                </Button>
              </label>
              {errors.logo && (
                <Typography variant="caption" color="error" display="block">
                  {errors.logo}
                </Typography>
              )}

              {/* thumbnail (existing or new) */}
              {currentLogoPreview ? (
                renderPreviewThumbnail(
                  currentLogoPreview,
                  "Logo Preview",
                  handleRemoveLogo,
                  !removeLogo
                )
              ) : (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 1, color: "#6b7280" }}
                >
                  No logo uploaded
                </Typography>
              )}
            </div>
            <div className="form-group subsidiary-upload-group">
              <label className="form-label">Payment QR Code (optional)</label>
              <div>
                <input
                  ref={qrInputRef}
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  style={{ display: "none" }}
                  id="edit-upload-qr"
                  type="file"
                  onChange={handleQrFileChange}
                />
                {currentQrName && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    File: {currentQrName}
                  </Typography>
                )}
                <label htmlFor="edit-upload-qr">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FiUpload />}
                    sx={{ color: "#4e4c4c", borderColor: "rgb(189,189,189)" }}
                  >
                    {qrFile || currentQrPreview
                      ? "Change Payment QR Code"
                      : "Upload Payment QR Code"}
                  </Button>
                </label>
                {errors.qrFile && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.qrFile}
                  </Typography>
                )}
                {currentQrPreview ? (
                  renderPreviewThumbnail(
                    currentQrPreview,
                    "Payment QR Code",
                    handleRemoveQr,
                    !removeQr
                  )
                ) : (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 1, color: "#6b7280" }}
                  >
                    No QR code uploaded
                  </Typography>
                )}
              </div>
            </div>
            </section>
            </div>

            <div className="form-footer subsidiary-form-footer">
              <div className="button-group">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={handleCancel}
                >
                  CANCEL
                </button>
                <button type="submit" className="btn btn-save">
                  SAVE
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity} // Ensures it takes the correct color
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
